import React, { useEffect, useRef } from 'react';
import './FamilyPlanModal.css';

interface FamilyPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FamilyPlanModal: React.FC<FamilyPlanModalProps> = ({
  isOpen,
  onClose
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Focus the close button when modal opens
      closeButtonRef.current?.focus();
      
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal closes
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="family-plan-modal-backdrop" 
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="family-modal-title"
      aria-describedby="family-modal-description"
    >
      <div className="family-plan-modal" ref={modalRef}>
        <div className="family-plan-modal-header">
          <div className="family-plan-modal-header-top">
            <h2 id="family-modal-title" className="family-plan-modal-title">
              Family
            </h2>
            <button
              ref={closeButtonRef}
              className="family-plan-modal-close"
              onClick={onClose}
              aria-label="Close modal"
            >
              ×
            </button>
          </div>
          <p id="family-modal-description" className="family-plan-modal-subtitle">
            Here are important reminders before you purchase.
          </p>
        </div>
        
        <div className="family-plan-modal-content">         
          <ul className="family-plan-modal-list">
            <li>
              Our Family Plan is ideal if there are at least three (3) persons traveling in a Family (e.g. husband and wife plus one child or one parent plus two children). If you wish to insure two adults, please get our Single Trip Individual Plan instead.
            </li>
            <li>
              The Family Plan is available if you plan to travel on the same departure and return trip with your family (e.g., you, your spouse and unlimited number of children), and you are riding on the same conveyance. Also, you and your legal spouse must be 18 to 75 years old and your children's age must range from 0 to 17 years old.
            </li>
            <li>
              This system can only accommodate families of up to seven (7) members. If you wish to insure more than 7 family members, you may opt to avail of our Single Trip Individual Plan for family members who are 18 years old and above. For further assistance, please e-mail{' '}
              <a 
                href="mailto:traveldirect_team@pacificcross.com.ph" 
                className="family-plan-modal-email-link"
              >
                traveldirect_team@pacificcross.com.ph
              </a>.
            </li>
            <li>
              Family plan applications that include legally adopted or non-legally adopted children require additional review before coverage can be issued. For assistance, please e-mail {' '}
              <a 
                href="mailto:traveldirect_team@pacificcross.com.ph" 
                className="family-plan-modal-email-link"
              >
                traveldirect_team@pacificcross.com.ph
              </a>.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FamilyPlanModal;